import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { IconBookmark, IconDeviceFloppy, IconTrash } from "@tabler/icons-react";
import {
  buildContractFiltersSnapshot,
  CONTRACTS_FILTER_CONTEXT,
  buildEmptyContractFiltersSnapshot,
} from "utils/contract-filters";

const SavedContractFilters = ({
  embedded = false,
  filters,
  searchTerm,
  importedStatusFilter,
  missingDataFilter,
  showDeletedContracts,
  onApplySnapshot,
  onDefaultLoaded,
  onInitialLoadComplete,
}) => {
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchSavedFilters = useCallback(async () => {
    try {
      const res = await fetch(
        `/backend/saved-list-filters/?context=${CONTRACTS_FILTER_CONTEXT}`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Falha ao carregar filtros salvos");
      const data = await res.json();
      setSavedFilters(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error("Erro ao carregar filtros salvos:", error);
      setSavedFilters([]);
    }
  }, []);

  const fetchDefaultFilter = useCallback(async () => {
    try {
      const res = await fetch(
        `/backend/saved-list-filters/default/?context=${CONTRACTS_FILTER_CONTEXT}`,
        { credentials: "include" }
      );
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Falha ao carregar filtro padrão");
      return await res.json();
    } catch (error) {
      console.error("Erro ao carregar filtro padrão:", error);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const [defaultFilter] = await Promise.all([
        fetchDefaultFilter(),
        fetchSavedFilters(),
      ]);

      if (!cancelled && defaultFilter?.filters_data && onApplySnapshot) {
        onApplySnapshot(defaultFilter.filters_data);
        setSelectedId(String(defaultFilter.id));
        if (onDefaultLoaded) {
          onDefaultLoaded();
        }
      }

      if (!cancelled && onInitialLoadComplete) {
        onInitialLoadComplete();
      }

      if (!cancelled) setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchDefaultFilter, fetchSavedFilters, onApplySnapshot, onDefaultLoaded, onInitialLoadComplete]);

  const getCurrentSnapshot = () =>
    buildContractFiltersSnapshot({
      filters,
      searchTerm,
      importedStatusFilter,
      missingDataFilter,
      showDeletedContracts,
    });

  const handleSelectFilter = (event) => {
    const id = event.target.value;
    setSelectedId(id);

    if (!id) {
      onApplySnapshot(buildEmptyContractFiltersSnapshot());
      return;
    }

    const saved = savedFilters.find((item) => String(item.id) === String(id));
    if (saved?.filters_data) {
      onApplySnapshot(saved.filters_data);
    }
  };

  const handleOpenSaveDialog = () => {
    const selected = savedFilters.find((item) => String(item.id) === String(selectedId));
    setSaveName(selected?.name || "");
    setSaveAsDefault(selected?.is_default || false);
    setSaveDialogOpen(true);
  };

  const handleSave = async () => {
    const name = saveName.trim();
    if (!name) {
      alert("Informe um nome para o conjunto de filtros.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        context: CONTRACTS_FILTER_CONTEXT,
        name,
        filters_data: getCurrentSnapshot(),
        is_default: saveAsDefault,
      };

      const selected = savedFilters.find((item) => String(item.id) === String(selectedId));
      const isUpdating = selected && selected.name === name;
      const url = isUpdating
        ? `/backend/saved-list-filters/${selected.id}/`
        : "/backend/saved-list-filters/";
      const method = isUpdating ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message =
          result.name?.[0] ||
          result.detail ||
          result.error ||
          "Não foi possível salvar o filtro.";
        throw new Error(message);
      }

      await fetchSavedFilters();
      setSelectedId(String(result.id));
      setSaveDialogOpen(false);
    } catch (error) {
      alert(`Erro ao salvar filtro:\n\n${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    const saved = savedFilters.find((item) => String(item.id) === String(selectedId));
    if (!saved) return;

    if (!window.confirm(`Excluir o filtro "${saved.name}"?`)) return;

    setDeleting(true);
    try {
      const res = await fetch(`/backend/saved-list-filters/${saved.id}/`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) {
        const result = await res.json().catch(() => ({}));
        throw new Error(result.detail || result.error || "Não foi possível excluir o filtro.");
      }
      setSelectedId("");
      await fetchSavedFilters();
    } catch (error) {
      alert(`Erro ao excluir filtro:\n\n${error.message || error}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: embedded ? 0 : 2,
        }}
      >
        {!embedded && <IconBookmark stroke={1.5} size="1.2rem" />}
        <Typography variant={embedded ? "subtitle2" : "subtitle1"} sx={{ mr: 1, color: embedded ? "text.secondary" : "inherit" }}>
          Filtros salvos
        </Typography>

        <FormControl size="small" sx={{ minWidth: 240 }} disabled={loading}>
          <InputLabel id="saved-contract-filters-label" shrink>
            Filtro salvo
          </InputLabel>
          <Select
            labelId="saved-contract-filters-label"
            value={selectedId}
            label="Filtro salvo"
            onChange={handleSelectFilter}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return loading ? "Carregando..." : "Nenhum";
              }
              const item = savedFilters.find((f) => String(f.id) === String(selected));
              if (!item) return "Nenhum";
              return item.is_default ? `${item.name} (padrão)` : item.name;
            }}
          >
            <MenuItem value="">
              <em>Nenhum</em>
            </MenuItem>
            {savedFilters.map((item) => (
              <MenuItem key={item.id} value={String(item.id)}>
                {item.name}
                {item.is_default ? " (padrão)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title="Salvar filtros atuais">
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenSaveDialog}
            startIcon={<IconDeviceFloppy stroke={1.5} size="1rem" />}
          >
            Salvar
          </Button>
        </Tooltip>

        <Tooltip title="Excluir filtro selecionado">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              disabled={!selectedId || deleting}
            >
              <IconTrash stroke={1.5} size="1.1rem" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Salvar filtros</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Salve o conjunto atual de filtros com um nome. Você pode marcá-lo como padrão para
            carregar automaticamente ao abrir esta tela.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Nome do filtro"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={saveAsDefault}
                onChange={(e) => setSaveAsDefault(e.target.checked)}
              />
            }
            label="Usar como padrão ao abrir a tela"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedContractFilters;
